import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  applyResolvedTheme,
  readThemePreference,
  resolveTheme,
  systemTheme,
  writeThemePreference,
} from '@signapass/shared';
import type { ResolvedTheme, ThemePreference } from '@signapass/shared';

interface ThemeValue {
  /** What the user asked for — including `system`, which is an answer. */
  preference: ThemePreference;
  /** What that resolves to right now. Components almost never need this. */
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

/**
 * Theme state for every surface in the SPA.
 *
 * The class on `<html>` is already correct before this mounts — the inline
 * bootstrap in `index.html` put it there. This provider exists to *change* it,
 * and to keep following the system when the preference is `system`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  /**
   * Follow the OS while the preference is `system`.
   *
   * Subscribed unconditionally rather than only under `system`, because the
   * listener is how `system` stays current for the moment the user switches
   * back to it — resubscribing on every preference change would miss a flip
   * that happened while they were on an explicit theme.
   */
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) =>
      setSystem(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved = resolveTheme(preference, system);

  useEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    writeThemePreference(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside a ThemeProvider');
  return value;
}
